import { useMemo, useState, useEffect, useReducer } from 'react';

import { useAtom } from 'jotai';

import { buildForecastRows } from './forecastTableRows';
import { buildUpdateFieldValues } from './helpers';
import { calcOptimization } from './optimize';
import { forecastData } from '../../MarketingMixModeling/forecast/forecastData';
import { summarizeForecastRows, fieldValuesToCurrentSpend } from '../../MarketingMixModeling/forecast/forecastHelpers';
import { optimizationAtom, isDirtyAtom, budgetAtom, optionalTargetAtom } from '../atoms';
import { BASELINE_ROW_KEY, KPI_METRIC_TEXT } from '../constants';
import { useProjectionPeriod } from '../ScenarioForm/hooks/usePeriods';

export const useForecast = ({
  fieldValues,
  responseCurves,
  featureStats,
  baseline,
  isConversionKPI,
}) => {
  const { projectionPeriod: numDays } = useProjectionPeriod();

  const forecastedData = useMemo(() => forecastData(responseCurves, fieldValues), [responseCurves, fieldValues]);

  const currentSpendValues = useMemo(() => fieldValuesToCurrentSpend(fieldValues), [fieldValues]);

  const currentForecastedData = useMemo(() => forecastData(responseCurves, currentSpendValues), [responseCurves, currentSpendValues]);

  const createEmptyRow = rows => Object.fromEntries(
    Object.keys(rows[0]).map(key => [key, 0]),
  );

  const forecastRows = useMemo(() => {
    if (!responseCurves || !featureStats) return [];

    const rows = buildForecastRows(fieldValues, responseCurves, currentSpendValues, currentForecastedData, forecastedData, numDays);

    const additionalRows = [
      {
        ...createEmptyRow(rows),
        key: isConversionKPI
          ? `${BASELINE_ROW_KEY}${KPI_METRIC_TEXT.conversions}`
          : `${BASELINE_ROW_KEY}${KPI_METRIC_TEXT.revenue}`,
        revenueAtCurrentValue: baseline / numDays,
        revenueAtTargetValue: baseline / numDays,
      },
      featureStats['Unclassified Spend'] && {
        ...createEmptyRow(rows),
        key: 'unclassified_spend',
        currentValue: featureStats['Unclassified Spend'].currentSpend || 0,
      },
    ].filter(Boolean);

    return [...rows, ...additionalRows];
  }, [fieldValues, responseCurves, currentSpendValues, currentForecastedData, forecastedData, numDays, baseline, featureStats]);

  const summary = useMemo(() => (
    summarizeForecastRows(forecastRows, featureStats, baseline, numDays)
  ), [forecastRows, numDays]);

  return {
    data: { forecastRows, baseline, summary },
  };
};

export const useOptimizationCalculation = ({ baseline, organicBaselineRevenue, responseCurves, constraints, isConversionKPI }) => {
  const [budget] = useAtom(budgetAtom);
  const [optionalTarget] = useAtom(optionalTargetAtom);
  const [optimization] = useAtom(optimizationAtom);
  const [isDirty, setIsDirty] = useAtom(isDirtyAtom);
  const [fieldValues, _setFieldValues] = useReducer((state, newState) => ({ ...state, ...newState }), {});
  const [optimizationError, setOptimizationError] = useState(null);

  // methods to set the field values and update optimization
  const setFieldValues = values => values && _setFieldValues(values);
  const submitForecast = () => setIsDirty(false);
  const updateFieldValues = buildUpdateFieldValues(fieldValues, setFieldValues);

  useEffect(() => {
    if (isDirty || !optimization) return;

    try {
      const values = calcOptimization(optimization, budget, constraints, baseline, organicBaselineRevenue, responseCurves, optionalTarget);
      setFieldValues(values);
      setOptimizationError(null);
    } catch (e) {
      setOptimizationError(e.message);
      setFieldValues(baseline);
    }
  }, [optimization, budget, optionalTarget, constraints, baseline, responseCurves, isConversionKPI, isDirty]);

  return {
    budget,
    optimization,
    isDirty,
    fieldValues,
    updateFieldValues,
    setFieldValues,
    submitForecast,
    optimizationError,
  };
};
